/**
 * Report Field Catalog — single source of truth for dynamic report form fields.
 * Used by ReportBuilderPage (admin config) and QuickReportModal (form rendering).
 */

import {
  Building2Icon,
  CalendarIcon,
  FileTextIcon,
  HardHatIcon,
  HashIcon,
  ClockIcon,
  StickyNoteIcon,
} from 'lucide-react';

// ─── Types ───────────────────────────────────────────────

export interface FieldConfig {
  visible: boolean;
  required: boolean;
}

export type FieldOverrides = Record<string, FieldConfig>;

export interface FieldCatalogEntry {
  key: string;
  labelKey: string;
  fieldType: 'select' | 'date' | 'number' | 'time' | 'textarea';
  icon: React.ComponentType<{ className?: string; size?: number }>;
  locked: boolean;
}

// ─── Catalog ─────────────────────────────────────────────

export const FIELD_CATALOG: FieldCatalogEntry[] = [
  { key: 'hospital', labelKey: 'reportBuilder.fields.hospital', fieldType: 'select', icon: Building2Icon, locked: true },
  { key: 'date', labelKey: 'reportBuilder.fields.date', fieldType: 'date', icon: CalendarIcon, locked: true },
  { key: 'treatmentType', labelKey: 'reportBuilder.fields.treatmentType', fieldType: 'select', icon: FileTextIcon, locked: false },
  { key: 'helmetType', labelKey: 'reportBuilder.fields.helmetType', fieldType: 'select', icon: HardHatIcon, locked: false },
  { key: 'quantity', labelKey: 'reportBuilder.fields.quantity', fieldType: 'number', icon: HashIcon, locked: false },
  { key: 'startTime', labelKey: 'reportBuilder.fields.startTime', fieldType: 'time', icon: ClockIcon, locked: false },
  { key: 'endTime', labelKey: 'reportBuilder.fields.endTime', fieldType: 'time', icon: ClockIcon, locked: false },
  { key: 'notes', labelKey: 'reportBuilder.fields.notes', fieldType: 'textarea', icon: StickyNoteIcon, locked: false },
];

export const FIELD_KEYS = FIELD_CATALOG.map((f) => f.key);

// ─── Defaults ────────────────────────────────────────────

export function getDefaultFieldOverrides(): FieldOverrides {
  return {
    hospital: { visible: true, required: true },
    date: { visible: true, required: true },
    treatmentType: { visible: true, required: false },
    helmetType: { visible: true, required: false },
    quantity: { visible: true, required: false },
    startTime: { visible: true, required: false },
    endTime: { visible: true, required: false },
    notes: { visible: true, required: false },
  };
}

// ─── Parse ───────────────────────────────────────────────

export function parseFieldOverrides(raw: string | null | undefined): FieldOverrides {
  const defaults = getDefaultFieldOverrides();

  if (!raw) return defaults;

  try {
    const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
    const result: FieldOverrides = { ...defaults };

    for (const key of FIELD_KEYS) {
      if (parsed[key]) {
        result[key] = {
          visible: typeof parsed[key].visible === 'boolean' ? parsed[key].visible : defaults[key].visible,
          required: typeof parsed[key].required === 'boolean' ? parsed[key].required : defaults[key].required,
        };
      }
    }

    // Enforce locked fields
    result.hospital = { visible: true, required: true };
    result.date = { visible: true, required: true };

    return result;
  } catch {
    return defaults;
  }
}
